import React, { useCallback, useEffect, useReducer, useRef, useState } from "react";
import { extractionEta } from "../pose/extractionEta";
import { Loader2, RotateCcw } from "lucide-react";
import {
  buildPoseFileJson,
  windowSampleTimes,
  SPLIT_FPS,
  blazePoseLandmarkName,
  FrameLandmarks,
} from "../pose/clientPose";
import { extractionReducer, initialExtractionState } from "../pose/extractionState";
import { MODEL_VERSION } from "../pose/mediapipeConfig";
import { createPoseLandmarker, poseModelProgress, PoseModelProgress, releasePoseLandmarker, subscribePoseModel } from "../pose/poseModel";
import { drawSkeleton, SkeletonPoint } from "../pose/skeleton";

/**
 * web-pose-extraction step 4: the on-device analysis. Runs MediaPipe PoseLandmarker over the
 * dropped video at the split cadence, DRAWING each frame with its skeleton as it is analysed, then
 * lets the user play/scrub the frames and, behind "Adjust joints", see per-joint values, drag a
 * joint to tweak it, and reset a frame to the detected pose. Emits the pose file JSON via
 * onPoseFile once every frame is done (null while it runs, on a restart, and when it fails — the
 * upload page keeps Upload grey until it is non-null), and the frames analysed so far via onFrames.
 */

// The joints worth showing values for (the ones the backend consumes).
const VALUE_JOINTS = [0, 11, 12, 13, 14, 15, 16, 23, 24, 25, 26, 27, 28];
const VALUE_JOINT_NAMES = VALUE_JOINTS.map((i) => blazePoseLandmarkName(i)!).filter(Boolean);

/** How often the page is told about new frames while the analysis runs (it re-guesses the technique
 * on each): every this many frames, and once more when the last one is done. */
const FRAMES_REPORT_EVERY = 5;

/** The frames analysed so far, for the page: the running technique guess and the progress line. */
export type ExtractionFrames = {
  frames: FrameLandmarks[];
  /** The video's own pixel size (landmarks are normalised to it). */
  width: number;
  height: number;
  /** How many frames there will be. */
  total: number;
  done: boolean;
};

type Props = {
  previewUrl: string;
  durationSec: number;
  onPoseFile: (json: string | null) => void;
  /** Told as frames are analysed (every few frames, and when the last is done). */
  onFrames?: (update: ExtractionFrames) => void;
  /** The cadence the server will split this upload at (frames pair with the split BY INDEX, so
   * the samples must sit exactly where the split frames will). The fixed 10 unless given. */
  fps?: number;
  /** A trimmed clip: the window starts here (seconds) and lasts durationSec. The server splits the same window. */
  startSec?: number;
};

/** What the step is waiting on before the first frame, with the download's progress when known. */
export function modelWaitText(p: PoseModelProgress): string {
  const mb = (b: number) => (b / 1048576).toFixed(0);
  switch (p.phase) {
    case "model":
      return p.totalBytes
        ? `Downloading the pose model… ${mb(p.loadedBytes)} of ${mb(p.totalBytes)} MB (a one-off)`
        : `Downloading the pose model… ${mb(p.loadedBytes)} MB so far (a one-off)`;
    case "warming":
      return "Starting the pose model…";
    case "warm":
      return "Analysing…";
    case "failed":
      return "The pose model could not be loaded — retrying…";
    default:
      return "Loading the pose runtime…";
  }
}

/** A frame's landmarks as canvas-pixel points keyed by landmark name, for drawSkeleton. */
function canvasPoints(frame: FrameLandmarks | undefined, width: number, height: number): Map<string, SkeletonPoint> {
  const points = new Map<string, SkeletonPoint>();
  for (const l of frame?.landmarks ?? []) {
    const name = blazePoseLandmarkName(l.index);
    if (name) points.set(name, { x: l.x * width, y: l.y * height });
  }
  return points;
}

export default function ExtractionStep({ previewUrl, durationSec, onPoseFile, onFrames, fps = SPLIT_FPS, startSec = 0 }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const [state, dispatch] = useReducer(extractionReducer, undefined, initialExtractionState);
  const [error, setError] = useState<string | null>(null);
  // The shared model load (started by the upload screen when it opened): what the step is waiting
  // on, and how far along, before the first frame can be analysed.
  const [model, setModel] = useState<PoseModelProgress>(poseModelProgress);
  useEffect(() => subscribePoseModel(setModel), []);
  // Whether the run has begun — before that the step is waiting on the VIDEO, not the model.
  const [started, setStarted] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [adjusting, setAdjusting] = useState(false);
  // The clip's own shape, once known: the canvas is shown at it, at most 70% of the window tall, so
  // a portrait phone clip does not push the technique list a screen and a half down the page.
  const [aspect, setAspect] = useState<number | null>(null);
  const dims = useRef({ w: 0, h: 0 });
  const dragging = useRef<number | null>(null);
  // A newer run (a retry, a new clip) makes an older one stop touching the screen and the page.
  const runId = useRef(0);

  /** The video's current frame with one frame's skeleton over it. */
  const paint = useCallback((frame: FrameLandmarks | undefined, tweaked: boolean) => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas || !video.videoWidth) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    const g = canvas.getContext("2d");
    if (!g) return;
    g.drawImage(video, 0, 0);
    drawSkeleton(g, canvasPoints(frame, canvas.width, canvas.height), canvas.width, {
      joint: tweaked ? "#FFA500" : undefined, // tweaked joints orange; otherwise each side's colour
      joints: VALUE_JOINT_NAMES,
      displayWidth: canvas.getBoundingClientRect().width,
    });
    setAspect((a) => a ?? video.videoWidth / video.videoHeight);
  }, []);

  const sampleTimes = windowSampleTimes(startSec, durationSec, fps);

  // ---- extraction run ---------------------------------------------------------------------------
  const runExtraction = useCallback(async () => {
    const video = videoRef.current;
    if (!video) return;
    const run = ++runId.current;
    const current = () => run === runId.current;
    onPoseFile(null);
    setError(null);
    setPlaying(false);
    dispatch({ type: "START", totalFrames: sampleTimes.length });
    try {
      // The model and runtime are shared and usually already in (the upload screen starts the
      // load when it opens); a landmarker over the downloaded bytes is quick to build.
      const landmarker = await createPoseLandmarker("VIDEO");
      dims.current = { w: video.videoWidth, h: video.videoHeight };
      const done: FrameLandmarks[] = [];
      const report = (finished: boolean) =>
        onFrames?.({ frames: done.slice(), width: dims.current.w, height: dims.current.h, total: sampleTimes.length, done: finished });
      for (const t of sampleTimes) {
        await seekTo(video, t);
        if (!current()) { releasePoseLandmarker(landmarker, "VIDEO"); return; }
        const res = landmarker.detectForVideo(video, Math.round(t * 1000));
        const lm = res.landmarks?.[0] ?? [];
        const frame: FrameLandmarks = {
          landmarks: lm
            .map((p: any, index: number) => ({ index, x: p.x, y: p.y, z: p.z, visibility: p.visibility }))
            .filter((p: any) => blazePoseLandmarkName(p.index) !== undefined),
        };
        // The video is sitting on this very frame: show it with what was just found on it.
        paint(frame, false);
        done.push(frame);
        dispatch({ type: "FRAME_DONE", frame });
        if (done.length % FRAMES_REPORT_EVERY === 0) report(false);
      }
      releasePoseLandmarker(landmarker, "VIDEO");
      if (!current()) return;
      report(true);
      dispatch({ type: "COMPLETE" });
    } catch (e: any) {
      if (!current()) return;
      // No pose, no upload: the page keeps Upload grey, and this step offers a retry.
      setError(e?.message ?? "The on-device analysis could not run.");
      onPoseFile(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewUrl, durationSec, fps, startSec]);

  useEffect(() => {
    // fresh video = fresh extraction
    setStarted(false);
    dispatch({ type: "START", totalFrames: 0 });
    const video = videoRef.current;
    if (!video) return;
    let begun = false;
    const start = () => { if (begun) return; begun = true; setStarted(true); runExtraction(); };
    // Metadata is enough to begin (seeking fetches each frame). NOT loadeddata alone: iOS Safari
    // does not reliably fire it for a hidden video that opens the same file as the visible preview
    // at the same instant (since #599 both open on the drop), so the run never began there and the
    // step sat on "Loading the pose model…" for ever. Any ready event starts it, the explicit
    // load() is what makes Safari fetch, and a video that stays silent is asked again.
    const READY_EVENTS = ["loadedmetadata", "loadeddata", "canplay"] as const;
    const nudge = () => { try { video.load(); } catch { /* jsdom has no load() */ } };
    let retry: number | undefined;
    if (video.readyState >= 1) start();
    else {
      READY_EVENTS.forEach((ev) => video.addEventListener(ev, start));
      nudge();
      retry = window.setInterval(() => { if (!begun) nudge(); }, 3000);
    }
    return () => {
      READY_EVENTS.forEach((ev) => video.removeEventListener(ev, start));
      if (retry !== undefined) window.clearInterval(retry);
      // A run still in flight for the old clip stops here. runId is a counter, not a DOM node: its
      // value at cleanup time is exactly the one wanted.
      // eslint-disable-next-line react-hooks/exhaustive-deps
      runId.current++;
    };
  }, [runExtraction]);

  // Emit the pose file whenever review-state frames change (incl. after tweaks).
  useEffect(() => {
    if (state.phase !== "review" || state.frames.length === 0) return;
    onPoseFile(
      buildPoseFileJson(state.frames, {
        captureWidth: dims.current.w,
        captureHeight: dims.current.h,
        modelVersion: MODEL_VERSION,
        fps,
      })
    );
  }, [state.phase, state.frames, onPoseFile, fps]);

  // ---- review: seek + draw ----------------------------------------------------------------------
  useEffect(() => {
    if (state.phase !== "review") return;
    const video = videoRef.current;
    if (!video) return;
    seekTo(video, sampleTimes[state.currentFrame] ?? 0).then(draw);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.currentFrame, state.phase, state.frames]);

  // play mode: advance frames on a timer at ~the split cadence
  useEffect(() => {
    if (!playing || state.phase !== "review") return;
    const id = setInterval(() => {
      dispatch({ type: "GOTO_FRAME", index: (state.currentFrame + 1) % state.frames.length });
    }, Math.round(1000 / fps));
    return () => clearInterval(id);
  }, [playing, state.phase, state.currentFrame, state.frames.length, fps]);

  const draw = useCallback(() => {
    if (state.phase !== "review") return;
    paint(state.frames[state.currentFrame], state.tweakedFrames.has(state.currentFrame));
  }, [state, paint]);

  useEffect(draw, [draw]);

  // ---- tweak: drag the nearest joint -------------------------------------------------------------
  const canvasPoint = (e: React.PointerEvent) => {
    const canvas = canvasRef.current!;
    const r = canvas.getBoundingClientRect();
    return { x: (e.clientX - r.left) / r.width, y: (e.clientY - r.top) / r.height };
  };

  const onPointerDown = (e: React.PointerEvent) => {
    if (state.phase !== "review" || !adjusting) return;
    const p = canvasPoint(e);
    const frame = state.frames[state.currentFrame];
    let best = -1, bestD = 0.04; // only grab within ~4% of the frame
    for (const l of frame?.landmarks ?? []) {
      if (!VALUE_JOINTS.includes(l.index)) continue;
      const d = Math.hypot(l.x - p.x, l.y - p.y);
      if (d < bestD) { bestD = d; best = l.index; }
    }
    if (best >= 0) {
      dragging.current = best;
      (e.target as HTMLElement).setPointerCapture(e.pointerId);
    }
  };

  const onPointerMove = (e: React.PointerEvent) => {
    if (dragging.current == null) return;
    const p = canvasPoint(e);
    dispatch({ type: "TWEAK", frameIndex: state.currentFrame, landmarkIndex: dragging.current, x: p.x, y: p.y });
  };

  const onPointerUp = () => { dragging.current = null; };

  // ---- render ------------------------------------------------------------------------------------
  const frame = state.frames[state.currentFrame];
  const percent = Math.round(state.progress * 100);
  // The pace starts at the first analysed frame (the model download is shown on its own), so the time left is honest.
  const firstFrameAt = useRef<number | null>(null);
  if (state.phase === "extracting" && state.frames.length > 0 && firstFrameAt.current == null) firstFrameAt.current = Date.now();
  if (state.phase !== "extracting") firstFrameAt.current = null;
  const eta =
    state.phase === "extracting" && firstFrameAt.current != null
      ? extractionEta(state.frames.length, state.totalFrames, Date.now() - firstFrameAt.current)
      : { timeLeft: null, slow: false };
  return (
    <div className="rounded-2xl border border-slate-200 p-4 space-y-3 bg-white">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-slate-700">On-device pose extraction</h2>
        {state.phase === "review" && state.tweakedFrames.has(state.currentFrame) && (
          <button
            type="button"
            onClick={() => dispatch({ type: "RESET_FRAME", frameIndex: state.currentFrame })}
            className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-700"
          >
            <RotateCcw className="h-3 w-3" /> reset frame to detected
          </button>
        )}
      </div>

      {/* hidden video drives seeking; the canvas is the display — the clip, with the skeleton found
          on each frame drawn over it as the analysis reaches that frame */}
      <video ref={videoRef} src={previewUrl} className="hidden" muted playsInline preload="auto" />
      <canvas
        ref={canvasRef}
        className={aspect ? "block mx-auto rounded-lg bg-black touch-none" : "w-full rounded-lg bg-black touch-none"}
        style={aspect ? { width: `min(100%, calc(70vh * ${aspect}))`, aspectRatio: String(aspect) } : undefined}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
      />

      {state.phase === "extracting" && !error && (
        <div className="space-y-1">
          <div className="flex items-center gap-2 text-xs text-slate-500">
            <Loader2 className="h-3 w-3 animate-spin" />
            {!started
              ? <>Opening the video…</>
              : state.frames.length === 0 && model.phase !== "warm"
                ? <>{modelWaitText(model)}</>
                : <>Analysing frame {Math.min(state.frames.length + 1, Math.max(state.totalFrames, 1))} of {state.totalFrames} · {percent}%{eta.timeLeft ? <> · {eta.timeLeft}</> : null}</>}
          </div>
          {eta.slow && (
            <p className="text-xs text-amber-700" data-testid="extraction-slow">
              This device analyses slowly. Keep this page open; it can take a few minutes.
            </p>
          )}
          <div className="h-2 rounded bg-slate-100 overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all" style={{ width: `${percent}%` }} />
          </div>
        </div>
      )}

      {state.phase === "review" && (
        <>
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => setPlaying((p) => !p)}
              className="px-3 py-1 text-xs rounded-full bg-slate-900 text-white"
            >
              {playing ? "pause" : "play"}
            </button>
            <input
              type="range"
              min={0}
              max={Math.max(0, state.frames.length - 1)}
              value={state.currentFrame}
              onChange={(e) => { setPlaying(false); dispatch({ type: "GOTO_FRAME", index: Number(e.target.value) }); }}
              className="flex-1 accent-emerald-500"
            />
            <span className="text-xs text-slate-500 tabular-nums">
              {state.currentFrame + 1}/{state.frames.length}
            </span>
          </div>
          <div className="flex items-center justify-between text-xs">
            <span className="text-emerald-700">All {state.frames.length} frames analysed on this device.</span>
            <button
              type="button"
              onClick={() => setAdjusting((a) => !a)}
              className="text-slate-500 underline hover:text-slate-700"
            >
              {adjusting ? "Done adjusting" : "Adjust joints"}
            </button>
          </div>
          {adjusting && (
            <>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-1 text-[11px] text-slate-600 font-mono">
                {(frame?.landmarks ?? [])
                  .filter((l) => VALUE_JOINTS.includes(l.index))
                  .map((l) => (
                    <div key={l.index} className="flex justify-between gap-2">
                      <span className="truncate">{blazePoseLandmarkName(l.index)}</span>
                      <span className="tabular-nums text-slate-400">
                        {(l.x * 100).toFixed(1)},{(l.y * 100).toFixed(1)} · {((l.visibility ?? 1) * 100).toFixed(0)}%
                      </span>
                    </div>
                  ))}
              </div>
              <p className="text-[11px] text-slate-400">
                drag a joint to correct it · tweaked frames show orange dots
              </p>
            </>
          )}
        </>
      )}

      {error && (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-red-200 bg-red-50 px-3 py-2">
          <p className="text-xs text-red-700">
            The on-device analysis could not finish ({error}). The video cannot be uploaded without it.
          </p>
          <button
            type="button"
            onClick={() => runExtraction()}
            className="shrink-0 rounded-full bg-slate-900 px-3 py-1 text-xs text-white"
          >
            Retry analysis
          </button>
        </div>
      )}
    </div>
  );
}

function seekTo(video: HTMLVideoElement, t: number): Promise<void> {
  return new Promise((resolve) => {
    if (Math.abs(video.currentTime - t) < 1 / 240 && video.readyState >= 2) return resolve();
    const done = () => { video.removeEventListener("seeked", done); resolve(); };
    video.addEventListener("seeked", done);
    video.currentTime = t;
  });
}
